/**
 * The shared vocabulary of a Dealbid form.
 *
 * A field error is keyed by the **dotted path core uses** — `deal.dealUrl`,
 * `seller.email`, `reason` — and every input in this repo is named with that same path.
 * That one decision is what lets a `VALIDATION_FAILED` response be rendered next to the
 * input that caused it without a translation table that could silently drift out of date
 * (canon `contracts/API.md` § Public submission shape).
 *
 * Client-side checks exist to answer quickly, not to be authoritative: core validates every
 * field again and its answer always wins. Nothing here may be relaxed on the assumption
 * that "the server will catch it" either — a form that lets an obvious mistake through
 * spends a round trip to say what it already knew.
 */
import type { ApiError } from "../api/client";

export type FieldErrors = Record<string, string>;

export function hasErrors(errors: FieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Turns core's `fields` array into per-input messages.
 *
 * Core's field messages are written for a form ("Enter a valid URL, including https://.")
 * and are shown as they are. A root-level issue — an empty `field`, which core uses when the
 * whole body was not an object — has no input to attach to and is deliberately dropped
 * here; the form's top-level message covers it.
 */
export function fieldErrorsFromApi(error: ApiError, fallback: string): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of error.fields) {
    if (issue.field === "") continue;
    errors[issue.field] = issue.message === "" ? fallback : issue.message;
  }
  return errors;
}
